import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  AppBar,
  Toolbar,
  Grid,
  TableContainer,
  Button,
  Menu,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TablePagination,
  TableSortLabel,
  Chip,
  Popover,
} from '@material-ui/core';
import axios from "axios";
import envConfig from "../../bin/env.config";
import { useSelector } from "react-redux";
import { SaveAlt as SaveAltIcon } from '@material-ui/icons';
import BarChartIcon from '@material-ui/icons/BarChart';
import FilterListIcon from '@material-ui/icons/FilterList';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import SendIcon from '@material-ui/icons/Send';
import ChartsDialog from './CustomerReport/ChartsOutOfDateDialog';
import ProductStatusByCityChart from './CustomerReport/ProductStatusByCityChart';
import { Close as CloseIcon } from '@material-ui/icons';
import CitySelector from './CustomerReport/CitySelector';
import BannerSelector from './CustomerReport/BannerSelector';
import StoreDateStatusCell from './CustomerReport/StoreDateStatusCell';
import { BACKGROUND_STATUS_COLORS } from '../../utility/statusConfig';

function OutOfDateStoreCell({ row, isNotUpdated, indent }) {
  const notUpdated = isNotUpdated(row.last_collected_date) ? 1 : 0;
  const [cellColor, setCellColor] = React.useState(
    notUpdated ? BACKGROUND_STATUS_COLORS[0] : ''
  );

  return (
    <TableCell
      style={{
        position: 'relative',
        backgroundColor: cellColor,
        cursor: row.external_link ? 'pointer' : 'default',
        color: notUpdated ? '#fff' : '#000',
        paddingLeft: indent ? 24 : undefined,
      }}
    >
      <StoreDateStatusCell
        cellData={{
          company_product_id: row.company_product_id,
          store_id: row.store_id,
          date: row.last_collected_date,
          not_updated: notUpdated,
          external_link: row.external_link,
          store_postal: row.store_postal,
        }}
        setCellColor={setCellColor}
      />
    </TableCell>
  );
}

function SendToDataNeededButton({ row, onResult }) {
  const token = useSelector((state) => state.session.token);
  const [sending, setSending] = useState(false);

  const handleClick = async (event) => {
    event.stopPropagation();
    if (sending) return;
    try {
      setSending(true);
      const response = await axios.post(
        `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/data-freshness-reporting/out-of-date-store-notify-slack`,
        {
          store_id: row.store_id,
          company_product_id: row.company_product_id,
          product_id: row.product_id,
          pmg_id: row.pmg_id,
          external_link: row.external_link,
          name: row.name,
          store_name: row.store_name,
          city: row.city,
        },
        {
          headers: {
            Authorization: token,
          },
        }
      );
      if (response.data && response.data.success) {
        onResult(true, 'Sent to #data-needed');
      } else {
        onResult(false, 'Failed to send to Slack');
      }
    } catch (error) {
      console.error('Error sending to Slack:', error);
      onResult(false, 'Failed to send to Slack');
    } finally {
      setSending(false);
    }
  };

  return (
    <IconButton
      size="small"
      onClick={handleClick}
      disabled={sending}
      title="Send to #data-needed"
      aria-label="Send to #data-needed"
    >
      <SendIcon fontSize="small" />
    </IconButton>
  );
}

const TABLE_COLUMNS = [
  { id: 'company_product_id', label: 'Company Product ID', width: 120, filterable: true },
  { id: 'product_id', label: 'Product ID', width: 90, filterable: true },
  { id: 'pmg_id', label: 'PMG ID', width: 80, filterable: true },
  { id: 'name', label: 'Product Name', width: 280 },
  { id: 'store_id', label: 'Store ID', width: 90, filterable: true },
  { id: 'store_name', label: 'Store', width: 120 },
  { id: 'city', label: 'City', width: 100 },
  { id: 'last_collected_date', label: 'Last Collected', width: 120 },
];

const PRODUCT_COLUMN_IDS = new Set(['company_product_id', 'product_id', 'pmg_id', 'name']);
const TABLE_COL_SPAN = TABLE_COLUMNS.length + 2;

function collectedTime(row) {
  if (!row.last_collected_date) return 0;
  const t = new Date(row.last_collected_date).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function rowMatchesStoreFilters(row, storeIdFilter, storeFilter) {
  if (storeIdFilter && !String(row.store_id).includes(storeIdFilter)) {
    return false;
  }
  if (storeFilter) {
    const lowerStoreFilter = storeFilter.toLowerCase();
    const storeMatch =
      String(row.store_id || '').includes(lowerStoreFilter) ||
      row.store_name?.toLowerCase().includes(lowerStoreFilter) ||
      row.store_address?.toLowerCase().includes(lowerStoreFilter) ||
      row.store_postal?.toLowerCase().includes(lowerStoreFilter);
    if (!storeMatch) return false;
  }
  return true;
}

function OutOfDateTable({
  data,
  filterText,
  storeFilter,
  datePeriod,
  fromDate,
  toDate,
  statusFilter,
  initialPmgIdFilter,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState('last_collected_date');
  const [order, setOrder] = useState('asc');
  const [companyProductIdFilter, setCompanyProductIdFilter] = useState('');
  const [productIdFilter, setProductIdFilter] = useState('');
  const [pmgIdFilter, setPmgIdFilter] = useState(initialPmgIdFilter || '');
  const [storeIdFilter, setStoreIdFilter] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [slackSnackbar, setSlackSnackbar] = useState({ open: false, message: '', error: false });

  const idFilterValues = {
    company_product_id: companyProductIdFilter,
    product_id: productIdFilter,
    pmg_id: pmgIdFilter,
    store_id: storeIdFilter,
  };

  const handleIdFilterChange = (columnId, value) => {
    if (columnId === 'company_product_id') setCompanyProductIdFilter(value);
    else if (columnId === 'product_id') setProductIdFilter(value);
    else if (columnId === 'pmg_id') setPmgIdFilter(value);
    else if (columnId === 'store_id') setStoreIdFilter(value);
  };

  useEffect(() => {
    if (initialPmgIdFilter) setPmgIdFilter(initialPmgIdFilter);
  }, [initialPmgIdFilter]);

  // Helper function to calculate not_updated based on date period
  const isNotUpdated = (lastCollectedDate) => {
    if (!lastCollectedDate) return true;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - datePeriod);
    return new Date(lastCollectedDate) < cutoffDate;
  };

  // Parse date boundaries for the range filter
  const { fromDateObj, toDateObj } = useMemo(() => {
    const fObj = fromDate ? new Date(fromDate) : null;
    const tObj = toDate ? new Date(toDate) : null;
    if (fObj) fObj.setHours(0, 0, 0, 0);
    if (tObj) tObj.setHours(23, 59, 59, 999);
    return { fromDateObj: fObj, toDateObj: tObj };
  }, [fromDate, toDate]);

  const isRowInDateRange = (row) => {
    if (!fromDateObj && !toDateObj) return true;
    if (!row.last_collected_date) return false;
    const d = new Date(row.last_collected_date);
    if (fromDateObj && d < fromDateObj) return false;
    if (toDateObj && d > toDateObj) return false;
    return true;
  };

  // Reset page when any filter changes
  useEffect(() => {
    setPage(0);
  }, [
    filterText,
    storeFilter,
    fromDate,
    toDate,
    statusFilter,
    companyProductIdFilter,
    productIdFilter,
    pmgIdFilter,
    storeIdFilter,
  ]);

  // Filter data (text filter + date range + status + ID column filters).
  // Store filters are applied after grouping so a matching store can be the parent
  // while sibling stores remain available on expand.
  const filteredData = useMemo(() => {
    const lowerFilter = filterText ? filterText.toLowerCase() : '';
    return data.filter(row => {
      if (companyProductIdFilter && !String(row.company_product_id).includes(companyProductIdFilter)) {
        return false;
      }
      if (productIdFilter && !String(row.product_id).includes(productIdFilter)) {
        return false;
      }
      if (pmgIdFilter && !String(row.pmg_id).includes(pmgIdFilter)) {
        return false;
      }
      if (lowerFilter) {
        const textMatch =
          row.name?.toLowerCase().includes(lowerFilter) ||
          row.city?.toLowerCase().includes(lowerFilter);
        if (!textMatch) return false;
      }
      if (!isRowInDateRange(row)) return false;
      if (statusFilter) {
        const notUpdated = isNotUpdated(row.last_collected_date);
        if (statusFilter === 'not_updated' && !notUpdated) return false;
        if (statusFilter === 'updated' && notUpdated) return false;
      }
      return true;
    });
  }, [
    data,
    filterText,
    fromDateObj,
    toDateObj,
    statusFilter,
    datePeriod,
    companyProductIdFilter,
    productIdFilter,
    pmgIdFilter,
  ]);

  const groupedData = useMemo(() => {
    const map = new Map();
    filteredData.forEach((row) => {
      const key = row.company_product_id;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });

    const hasStoreFilter = Boolean(storeIdFilter || storeFilter);

    return Array.from(map.values()).reduce((groups, stores) => {
      const sortedStores = [...stores].sort((a, b) => collectedTime(a) - collectedTime(b));
      if (hasStoreFilter) {
        const matches = sortedStores.filter((row) =>
          rowMatchesStoreFilters(row, storeIdFilter, storeFilter)
        );
        if (matches.length === 0) return groups;
        const parent = matches[0];
        const children = sortedStores.filter((row) => row.store_id !== parent.store_id);
        groups.push({ parent, stores: [parent, ...children] });
        return groups;
      }
      const parent = sortedStores[0];
      groups.push({ parent, stores: sortedStores });
      return groups;
    }, []);
  }, [filteredData, storeIdFilter, storeFilter]);

  const sortedGroups = useMemo(() => {
    const comparator = (a, b) => {
      let aVal = a.parent[orderBy];
      let bVal = b.parent[orderBy];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (orderBy === 'last_collected_date') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    };
    return [...groupedData].sort(comparator);
  }, [groupedData, orderBy, order]);

  const paginatedGroups = useMemo(() => {
    return sortedGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedGroups, page, rowsPerPage]);

  const toggleExpand = (companyProductId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyProductId)) {
        next.delete(companyProductId);
      } else {
        next.add(companyProductId);
      }
      return next;
    });
  };

  const handleSlackResult = (ok, message) => {
    setSlackSnackbar({ open: true, message, error: !ok });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const renderDataCell = (row, col, isChild = false) => {
    if (isChild && PRODUCT_COLUMN_IDS.has(col.id)) {
      return <TableCell key={col.id} />;
    }
    const indentStyle = isChild ? { paddingLeft: 24 } : undefined;
    switch (col.id) {
      case 'company_product_id':
        return <TableCell key={col.id}>{row.company_product_id}</TableCell>;
      case 'product_id':
        return (
          <TableCell
            key={col.id}
            style={{ cursor: 'pointer' }}
            onClick={() => window.open(`/product/${row.product_id}`, '_blank')}
          >
            {row.product_id}
          </TableCell>
        );
      case 'pmg_id':
        return (
          <TableCell
            key={col.id}
            style={{ cursor: 'pointer' }}
            onClick={() => window.open(`/matching-group/${row.pmg_id}`, '_blank')}
          >
            {row.pmg_id}
          </TableCell>
        );
      case 'name':
        return (
          <TableCell
            key={col.id}
            style={{
              cursor: 'pointer',
              maxWidth: 280,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            onClick={() => {
              if (row.store_postal) {
                navigator.clipboard.writeText(String(row.store_postal));
              }
              window.open(row.external_link, '_blank');
            }}
            title={row.name}
          >
            {row.name}
          </TableCell>
        );
      case 'store_id':
        return <TableCell key={col.id} style={indentStyle}>{row.store_id}</TableCell>;
      case 'store_name':
        return (
          <TableCell key={col.id} style={indentStyle}>
            {row.store_name}{row.store_postal ? ` (${row.store_postal})` : ''}<br />
            <i>{row.store_address}</i>
          </TableCell>
        );
      case 'city':
        return <TableCell key={col.id} style={indentStyle}>{row.city}</TableCell>;
      default:
        return null;
    }
  };

  const renderRowCells = (row, isChild = false) =>
    TABLE_COLUMNS.map((col) => {
      if (col.id === 'last_collected_date') {
        return (
          <OutOfDateStoreCell
            key={col.id}
            row={row}
            isNotUpdated={isNotUpdated}
            indent={isChild}
          />
        );
      }
      return renderDataCell(row, col, isChild);
    });

  // Export to CSV — all filtered stores, not only parent rows
  const handleExportCSV = () => {
    let csv = 'Company Product ID,Product ID,PMG ID,Product Name,Store ID,Store Name,Postal Code,City,Last Collected Date,Status,Link\n';
    groupedData.forEach((group) => {
      group.stores.forEach((row) => {
        const rowData = [
          row.company_product_id || '',
          row.product_id || '',
          row.pmg_id,
          '"' + (row.name || '').replace(/"/g, '""') + '"',
          row.store_id || '',
          '"' + (row.store_name || '').replace(/"/g, '""') + '"',
          '"' + (row.store_postal || '').replace(/"/g, '""') + '"',
          '"' + (row.city || '').replace(/"/g, '""') + '"',
          row.last_collected_date ? new Date(row.last_collected_date).toLocaleDateString() : '',
          isNotUpdated(row.last_collected_date) ? 'Not Updated' : 'Updated',
          row.external_link || ''
        ];
        csv += rowData.join(',') + '\n';
      });
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'out_of_date_store_products.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const notUpdatedCount = groupedData.filter(g => isNotUpdated(g.parent.last_collected_date)).length;
  const updatedCount = groupedData.filter(g => !isNotUpdated(g.parent.last_collected_date)).length;

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" p={1} style={{ backgroundColor: '#f5f5f5' }}>
        <Box display="flex" gap={2} alignItems="center">
          <Typography variant="body2">
            Total: <strong>{groupedData.length}</strong>
          </Typography>
          <Chip 
            label={`Not Updated: ${notUpdatedCount}`} 
            size="small" 
            style={{ backgroundColor: '#f44336', color: '#fff', marginLeft: 8 }} 
          />
          <Chip 
            label={`Updated: ${updatedCount}`} 
            size="small" 
            style={{ backgroundColor: '#2196f3', color: '#fff', marginLeft: 8 }} 
          />
        </Box>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<SaveAltIcon />}
          onClick={handleExportCSV}
        >
          Export CSV
        </Button>
      </Box>
      <TableContainer style={{ maxHeight: 'calc(100vh - 220px)' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: 140 }} />
              {TABLE_COLUMNS.map(col => (
                <TableCell
                  key={col.id}
                  style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: col.width }}
                >
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: 56 }}>
                Slack
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ backgroundColor: '#f5f5f5', padding: '4px 8px', width: 140 }} />
              {TABLE_COLUMNS.map(col => (
                <TableCell
                  key={`filter-${col.id}`}
                  style={{ backgroundColor: '#f5f5f5', padding: '4px 8px', width: col.width }}
                >
                  {col.filterable ? (
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="Filter"
                      value={idFilterValues[col.id]}
                      onChange={(e) => handleIdFilterChange(col.id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      fullWidth
                    />
                  ) : null}
                </TableCell>
              ))}
              <TableCell style={{ backgroundColor: '#f5f5f5', padding: '4px 8px', width: 56 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedGroups.map((group) => {
              const { parent, stores } = group;
              const canExpand = stores.length > 1;
              const isExpanded = expandedIds.has(parent.company_product_id);
              const children = stores.slice(1);
              return (
                <React.Fragment key={parent.company_product_id}>
                  <TableRow hover>
                    <TableCell style={{ whiteSpace: 'nowrap', width: 140 }}>
                      {canExpand ? (
                        <Box display="flex" alignItems="center">
                          <IconButton
                            size="small"
                            onClick={() => toggleExpand(parent.company_product_id)}
                            aria-label={isExpanded ? 'Collapse stores' : 'Expand stores'}
                          >
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                          <Chip
                            size="small"
                            label={`${stores.length} stores`}
                          />
                        </Box>
                      ) : null}
                    </TableCell>
                    {renderRowCells(parent)}
                    <TableCell>
                      <SendToDataNeededButton row={parent} onResult={handleSlackResult} />
                    </TableCell>
                  </TableRow>
                  {isExpanded && children.map((child) => (
                    <TableRow
                      key={`${child.company_product_id}-${child.store_id}`}
                      hover
                      style={{ backgroundColor: '#fafafa' }}
                    >
                      <TableCell />
                      {renderRowCells(child, true)}
                      <TableCell>
                        <SendToDataNeededButton row={child} onResult={handleSlackResult} />
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
            {paginatedGroups.length === 0 && (
              <TableRow>
                <TableCell colSpan={TABLE_COL_SPAN} align="center">
                  <Typography color="textSecondary">No data found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sortedGroups.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
      <Snackbar
        open={slackSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setSlackSnackbar((prev) => ({ ...prev, open: false }))}
        message={slackSnackbar.message}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  );
}

export default function CompanyReportToolOutOfDateStorePage(props) {
  const history = useHistory();
  const location = useLocation();
  const token = useSelector((state) => state.session.token);
  const user_type_id = props.match.params.user_type_id;
  const project_id = props.match.params.project_id;
  const [last_cache_update, setLastCacheUpdate] = useState(null);
  const [showError, setShowError] = useState(false);
  const [outOfDateProducts, setOutOfDateProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedBanners, setSelectedBanners] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [datePeriod, setDatePeriod] = useState(14);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('not_updated');
  const [chartsDialogOpen, setChartsDialogOpen] = useState(false);
  const [chartMenuAnchor, setChartMenuAnchor] = useState(null);
  const [dateFilterAnchor, setDateFilterAnchor] = useState(null);
  const [statusByCityDialogOpen, setStatusByCityDialogOpen] = useState(false);
  const [initialPmgIdFilter, setInitialPmgIdFilter] = useState('');

  const PERIOD_LABELS = { 14: '14 Days', 30: '1 Month', 90: '3 Months', 180: '6 Months' };

  const dateFilterLabel = useMemo(() => {
    let label = PERIOD_LABELS[datePeriod] || `${datePeriod} Days`;
    if (fromDate || toDate) {
      label += ` | ${fromDate || '...'} – ${toDate || '...'}`;
    }
    return label;
  }, [datePeriod, fromDate, toDate]);

  const checkCacheReport = async () => {
    try {
      const response = await axios.post(
        `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/data-freshness-reporting/check-report-data`,
        {
          user_type_id: user_type_id,
          report_name: 'customer_project_ood'
        },
        {
          headers: {
            Authorization: token,
          },
        }
      );
      setLastCacheUpdate(response.data.data);
      if (response.data.data && response.data.data.id === 0) {
        setShowError(true);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error checking cache report:", error);
      setShowError(true);
      return false;
    }
  };

  const fetchOutOfDateProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/data-freshness-reporting/out-of-date-store-summary`,
        {
          user_type_id: user_type_id,
          city: selectedCity,
          banners: selectedBanners,
          date_period: datePeriod,
        },
        {
          headers: {
            Authorization: token,
          },         
        }
      );
      setOutOfDateProducts(response.data.data || []);
    } catch (error) {
      console.error("Error fetching out of date products:", error);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pmgId = params.get('pmg_id');
    const companyId = params.get('company_id');
    const period = params.get('datePeriod');
    const status = params.get('status');
    if (pmgId) setInitialPmgIdFilter(pmgId);
    if (companyId) setSelectedBanners([companyId]);
    if (period && !Number.isNaN(Number(period))) setDatePeriod(Number(period));
    if (status === 'not_updated' || status === 'updated') setStatusFilter(status);
  }, [location.search]);

  useEffect(() => {
    if (project_id) {
      checkCacheReport();
    }
  }, [project_id]);

  useEffect(() => {
    fetchOutOfDateProducts();
  }, [selectedCity, selectedBanners, datePeriod]);

  const handleErrorClose = () => {
    setShowError(false);
  };

  const handleChartMenuClick = (event) => {
    setChartMenuAnchor(event.currentTarget);
  };

  const handleChartMenuClose = () => {
    setChartMenuAnchor(null);
  };

  const handleDateFilterClick = (event) => {
    setDateFilterAnchor(event.currentTarget);
  };

  const handleDateFilterClose = () => {
    setDateFilterAnchor(null);
  };

  const handleOutOfDateDataAgeChart = () => {
    handleChartMenuClose();
    setChartsDialogOpen(true);
  };

  const handleStatusByCityChart = () => {
    handleChartMenuClose();
    setStatusByCityDialogOpen(true);
  };

  const handleCheckedReportPage = () => {
    handleChartMenuClose();
    window.open(`/company-report-tool-checked-report/${user_type_id}/${project_id}`, '_blank');
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
  };

  const handleBannerSelect = (banner) => {
    setSelectedBanners(prev => {
      if (banner === '') {
        return []; // Clear all selections
      }
      if (prev.includes(banner)) {
        return prev.filter(b => b !== banner); // Remove if already selected
      }
      return [...prev, banner]; // Add new selection
    });
  };

  return (
    <>
      <AppBar position="sticky" color="inherit">
        <Toolbar>         
          <Typography variant="h6" noWrap style={{ marginRight: '20px' }}>
            Not updated price detail by Store
          </Typography>
          <CitySelector
            selectedCity={selectedCity}
            onCitySelect={handleCitySelect}
            project_id={project_id}
            token={token}
          />
          <BannerSelector
            project_id={project_id}
            token={token}
            selectedBanners={selectedBanners}
            onBannerSelect={handleBannerSelect}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handleDateFilterClick}
            endIcon={<FilterListIcon />}
            style={{ marginRight: 8, textTransform: 'none' }}
          >
            {dateFilterLabel}
          </Button>
          <Popover
            open={Boolean(dateFilterAnchor)}
            anchorEl={dateFilterAnchor}
            onClose={handleDateFilterClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box p={2} minWidth={280}>
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label="Not updated threshold"
                value={datePeriod}
                onChange={(e) => setDatePeriod(Number(e.target.value))}
                InputLabelProps={{ shrink: true }}
                margin="dense"
              >
                <MenuItem value={14}>14 Days</MenuItem>
                <MenuItem value={30}>1 Month</MenuItem>
                <MenuItem value={90}>3 Months</MenuItem>
                <MenuItem value={180}>6 Months</MenuItem>
              </TextField>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                type="date"
                label="From date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                margin="dense"
              />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                type="date"
                label="To date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                margin="dense"
              />
              {(fromDate || toDate) && (
                <Button
                  size="small"
                  onClick={() => { setFromDate(''); setToDate(''); }}
                  style={{ marginTop: 8 }}
                >
                  Clear dates
                </Button>
              )}
            </Box>
          </Popover>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Filter by name or city"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            style={{ minWidth: 220, marginRight: 8 }}
          />
          <TextField
            variant="outlined"
            size="small"
            placeholder="Filter by store ID, name, address, or postal code"
            value={storeFilter}
            onChange={(e) => setStoreFilter(e.target.value)}
            style={{ minWidth: 260 }}
          />
          <TextField
            select
            size="small"
            variant="outlined"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ minWidth: 140, marginLeft: 8 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="not_updated">Not Updated</MenuItem>
            <MenuItem value="updated">Updated</MenuItem>
          </TextField>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<BarChartIcon />}
            style={{ marginLeft: 'auto' }}
            onClick={handleChartMenuClick}
          >
            Summary Report
          </Button>
          <Menu
            anchorEl={chartMenuAnchor}
            keepMounted
            open={Boolean(chartMenuAnchor)}
            onClose={handleChartMenuClose}
          >
            <MenuItem onClick={handleOutOfDateDataAgeChart}>Not updated Data Age Chart</MenuItem>
            <MenuItem onClick={handleStatusByCityChart}>Not updated Status by City</MenuItem>
            <MenuItem onClick={handleCheckedReportPage}>Not updated Checked Report</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <TableContainer  style={{ maxHeight: 'calc(100vh - 122px)', width:'100%' }}>
              {outOfDateProducts.length > 0 ? (
                <OutOfDateTable
                  data={outOfDateProducts}
                  filterText={filterText}
                  storeFilter={storeFilter}
                  datePeriod={datePeriod}
                  fromDate={fromDate}
                  toDate={toDate}
                  statusFilter={statusFilter}
                  initialPmgIdFilter={initialPmgIdFilter}
                />
              ) : (
                <Typography style={{ padding: '16px' }}>No out of date products found.</Typography>
              )}
            </TableContainer>
          )}
        </Grid>
      </Grid>
      <ChartsDialog
        open={chartsDialogOpen}
        onClose={() => setChartsDialogOpen(false)}
        outOfDateProducts={outOfDateProducts}
      />
      <Dialog
        open={statusByCityDialogOpen}
        onClose={() => setStatusByCityDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>Product Status by City</span>
            <IconButton aria-label="close" onClick={() => setStatusByCityDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <ProductStatusByCityChart 
            user_type_id={user_type_id}
            selectedCity={selectedCity}
            selectedBanners={selectedBanners}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        message="Error loading data. Please try again."
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  );
} 